import { Show, useObservable } from "@legendapp/state/react"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { cn, ENV, sharedStyles, trpc } from "@src/utils"
import { ActivityIndicator, Modal, Platform, Pressable, View } from "react-native"
import { $Pressable } from "@legendapp/state/react-native"
import { Text, TextInput } from "./core"
import { SafeAreaView } from "react-native-safe-area-context"
import { useState } from "react"
import { useNumCols } from "@src/hooks"
import { List } from "./list"
import { ar } from "@src/i18n/ar"
import { Image } from "expo-image"
import { Header } from "./Header"

export type PickerProps = {
  value?: number[]
  onChange: (ids: number[]) => void
  className?: string
  mode?: "single" | "multiple"
}

export function CollectionPicker({ value = [], onChange, className, mode = "multiple" }: PickerProps) {
  const collections = trpc.menu.collections.useQuery()

  const selectedNames = collections.data
    ?.filter((c) => value.includes(c.id))
    .map((c) => c.name)
    .join(", ")

  const displayText = selectedNames || ar.selectCollection
  const visible = useObservable(false)

  function hide() {
    visible.set(false)
  }

  function handleChange(ids: number[]) {
    onChange(ids)
    hide()
  }

  return (
    <>
      <Pressable
        className={cn("min-h-[45px] bg-white border border-gray-300 rounded px-4 justify-center active:opacity-70 flex-row items-center", className)}
        onPress={() => visible.set(true)}
      >
        <Text className={cn("flex-1", !selectedNames && "text-gray-400")} numberOfLines={1}>
          {displayText}
        </Text>
        <View className="absolute left-3">
          <MIcon name="chevron-down" size={24} color="#999" />
        </View>
      </Pressable>

      <Show if={visible}>
        <CollectionPickModal value={value} onChange={handleChange} hide={hide} mode={mode} />
      </Show>
    </>
  )
}

export function CollectionPickModal({ value = [], onChange, hide, mode = "multiple" }: PickerProps & { hide: () => void }) {
  const [searchText, setSearchText] = useState("")
  const collections = trpc.menu.collections.useQuery()
  const numCols = Math.max(2, useNumCols())
  const value$ = useObservable(value)

  const filteredCollections = collections.data?.filter((c) => c.name.toLowerCase().includes(searchText.toLowerCase())) || []

  const handleItemPress = (id: number) => {
    if (mode === "single") {
      onChange([id])
    }

    if (mode === "multiple") {
      value$.set((v) => (v.includes(id) ? v.filter((v) => v !== id) : [...v, id]))
    }
  }

  const header = (
    <Header
      title={ar.selectCollection}
      withoutInsets={Platform.OS === "ios"}
      right={
        <View className="flex-row h-full">
          <Pressable onPress={() => onChange(value$.get())} className="h-full justify-center items-center px-4">
            <Text className="text-blue-500 font-bold">{ar.done}</Text>
          </Pressable>
          <Pressable onPress={hide} className="h-full justify-center items-center px-4">
            <MIcon name="close" size={24} color="#333" />
          </Pressable>
        </View>
      }
      canGoBack={false}
    />
  )

  return (
    <Modal animationType="slide" presentationStyle="formSheet" onRequestClose={hide} className="bg-gray-50" allowSwipeDismissal>
      {header}

      <View className="w-full max-w-content mx-auto px-2 py-2">
        <TextInput autoFocus value={searchText} onChangeText={setSearchText} placeholder={ar.search} />
      </View>

      <List
        data={filteredCollections}
        numColumns={numCols}
        contentContainerStyle={sharedStyles.contentContainer}
        key={`collection-${collections.data?.length}`}
        keyExtractor={(item) => item.id.toString()}
        ListEmptyComponent={collections.isLoading ? <ActivityIndicator size="small" className="py-2" /> : <Text className="text-center py-8 text-gray-500">{ar.noSections}</Text>}
        renderItem={({ item }) => {
          const image = item.images?.at(-1)

          return (
            <$Pressable
              $className={() => cn("p-2 m-1 border-b border-gray-100 active:bg-gray-50 justify-center items-center relative", value$.get().includes(item.id) && "bg-blue-50")}
              onPress={() => handleItemPress(item.id)}
            >
              <Show if={() => value$.get().includes(item.id)}>
                <View className="absolute top-2 right-2 z-10 bg-blue-500 rounded-full pb-0.5">
                  <MIcon name="check" size={16} color="white" />
                </View>
              </Show>

              {image ? (
                <Image source={`${ENV.R2_URL}/${image.key}`} style={{ width: 100, height: 100, marginBottom: 8 }} transition={250} loading="lazy" cachePolicy="memory" />
              ) : (
                <View style={{ width: 100, height: 100, marginBottom: 8 }} />
              )}
              <Text className="text-sm font-medium text-gray-800">{item.name}</Text>
            </$Pressable>
          )
        }}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />
    </Modal>
  )
}
